import { ChangeEvent, FormEvent } from 'react';
import { white } from 'config/colors';
import { useHistory } from 'react-router-dom';
import { IBasicSession } from 'types/basicSession';
import CircularProgress from '@mui/material/CircularProgress';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { useMarkSessionCompleteMutation } from 'hooks/sessions';

interface IEditSessionFormProps {
  isUpdating: boolean;
  editedSessionData: IBasicSession;
  handleSubmit: (e: FormEvent<HTMLFormElement>) => void;
  setEditedSessionData: (sessionData: IBasicSession) => void;
}

const EditSessionForm = ({
  isUpdating,
  editedSessionData,
  setEditedSessionData,
  handleSubmit,
}: IEditSessionFormProps) => {
  const history = useHistory();

  const { mutateAsync: markSessionComplete, isLoading: isMarkingSessionComplete } =
    useMarkSessionCompleteMutation();

  const handleOnChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setEditedSessionData({ ...editedSessionData, [name]: value });
  };

  const sessionIsDisabled = editedSessionData.status === 'completed';
  const daysOfTheWeek = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];

  const handleOnChangeAvailability = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      if (editedSessionData.availability?.day.length === 3) return;
      setEditedSessionData({
        ...editedSessionData,
        availability: {
          day: [...(editedSessionData.availability?.day || []), e.target.value],
          times: editedSessionData.availability?.times || { start: '', end: '' },
        },
      });
    } else {
      setEditedSessionData({
        ...editedSessionData,
        availability: {
          day: editedSessionData.availability?.day.filter((day) => day !== e.target.value) || [],
          times: editedSessionData.availability?.times || { start: '', end: '' },
        },
      });
    }
  };

  const markCompleteClicked = (row: IBasicSession) => {
    if (!row._id) return;
    markSessionComplete(row._id);
  };

  const handleManageLiveClassBtnClick = () => {
    history.push(`/dashboard/create-live-class/${editedSessionData._id}`);
  };

  return (
    <div>
      <form className="d-flex flex-column" onSubmit={handleSubmit}>
        <label>
          Session title:
          <input
            name="title"
            required
            type="text"
            value={editedSessionData.title}
            onChange={handleOnChange}
            className="form-control mb-2"
            disabled={sessionIsDisabled}
          />
        </label>

        <label>
          Description:
          <input
            name="description"
            required
            type="text"
            value={editedSessionData.description}
            onChange={handleOnChange}
            className="form-control mb-2"
            disabled={sessionIsDisabled}
          />
        </label>

        <label>
          Status:
          <select
            name="status"
            required
            value={editedSessionData.status}
            onChange={handleOnChange}
            className="form-control mb-2"
            disabled={sessionIsDisabled}
          >
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
          </select>
        </label>

        <label>
          Number of kids:
          <input
            name="numberOfKids"
            required
            type="number"
            disabled
            value={editedSessionData.numberOfKids}
            onChange={handleOnChange}
            className="form-control mb-2"
          />
        </label>

        <label>
          Sessions Per Week:
          <input
            name="sessionsPerWeek"
            required
            type="number"
            min={1}
            value={editedSessionData.sessionsPerWeek}
            onChange={handleOnChange}
            className="form-control mb-2"
            disabled={sessionIsDisabled}
          />
        </label>

        <label>
          Duration:
          <select
            name="duration"
            required
            disabled
            value={editedSessionData.duration}
            onChange={handleOnChange}
            className="form-control mb-2"
          >
            <option value="1h">1 hour</option>
            <option value="2h">2 hours</option>
          </select>
        </label>

        <label>
          Time:
          <div className="flex">
            <p>Start time: {editedSessionData.availability?.times.start}</p>
            <p style={{ marginLeft: '10px' }}>
              End time: {editedSessionData.availability?.times.end}
            </p>
          </div>
        </label>

        <label>
          Availability:
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              rowGap: '1.5rem',
              padding: '1rem 1.5rem 0px',
            }}
          >
            {daysOfTheWeek.map((day) => (
              <label key={day} style={{ width: '33%' }}>
                <input
                  type="checkbox"
                  name="availability"
                  value={day}
                  checked={editedSessionData.availability?.day.includes(day)}
                  onChange={handleOnChangeAvailability}
                  className="form-check-input"
                  disabled={sessionIsDisabled}
                />
                {day}
              </label>
            ))}
          </div>
        </label>

        <div className="mt-4 d-flex flex-row align-items-center gap-3 justify-content-between">
          <div className="d-flex flex-row align-items-center gap-3">
            <button
              type="submit"
              className="btn btn-primary btn-block"
              disabled={sessionIsDisabled || isMarkingSessionComplete}
            >
              {isUpdating ? (
                <CircularProgress sx={{ color: white }} size={24} thickness={8} />
              ) : (
                'Update session'
              )}
            </button>

            <button
              type="button"
              className="btn btn-success btn-block"
              disabled={sessionIsDisabled || isMarkingSessionComplete}
              onClick={() => markCompleteClicked(editedSessionData)}
            >
              {sessionIsDisabled ? 'Session completed' : 'Mark Complete'}
            </button>
          </div>

          <button
            type="button"
            className="btn btn-dark btn-block"
            disabled={sessionIsDisabled || isMarkingSessionComplete}
            onClick={handleManageLiveClassBtnClick}
          >
            <AddOutlinedIcon />
            Create live class
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditSessionForm;
